import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AnimatedIcon } from '../../components/icons';
import { adminService } from '../../services/adminService';
import { ServiceRequest, RequestState, SURVEY_SERVICE_TYPES, CommissionSummary } from '../../types';
import { PieChart, PIE_COLORS } from '../../components/PieChart';
import { WEB_STATUS_COLORS } from '../../utils/statusPresentation';

const CSS = `
.zs-stat-card {
  position: relative;
  overflow: hidden;
  height: 100%;
  border-radius: 12px;
  border: 1px solid var(--sc-border);
  background: var(--sc-bg-secondary);
  box-shadow: var(--sc-shadow-sm);
  padding: 14px 16px;
  text-decoration: none;
  display: block;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.zs-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--sc-shadow-md);
}
.zs-stat-card-circle {
  position: absolute;
  top: -16px;
  right: -16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.zs-stat-card:hover .zs-stat-card-circle {
  transform: scale(1.15);
}
.zs-stat-card-iconbox {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.zs-stat-card:hover .zs-stat-card-iconbox {
  transform: scale(1.06) rotate(-3deg);
}
`;

const STATUS_COLORS = WEB_STATUS_COLORS;

export const AdminSurveyHistory: React.FC = () => {
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [summary, setSummary] = useState<CommissionSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<'date' | 'status' | 'type'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    Promise.all([
      adminService.listRequests({ page: 1 }).then((r) => setRequests(r.results ?? [])),
      adminService.getCommissionSummary().then(setSummary).catch(() => {}),
    ]).finally(() => setIsLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let items = [...requests];

    if (statusFilter !== 'ALL') {
      items = items.filter((r) => r.status === statusFilter);
    }
    if (serviceTypeFilter) {
      items = items.filter((r) => r.service_type === serviceTypeFilter);
    }
    if (dateFrom) {
      items = items.filter((r) => r.created_at >= dateFrom);
    }
    if (dateTo) {
      const end = dateTo + 'T23:59:59';
      items = items.filter((r) => r.created_at <= end);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter((r) =>
        r.address.toLowerCase().includes(q) ||
        r.service_type.toLowerCase().includes(q) ||
        r.client?.first_name?.toLowerCase().includes(q) ||
        r.client?.last_name?.toLowerCase().includes(q) ||
        (r.assigned_surveyor?.first_name || '').toLowerCase().includes(q) ||
        (r.assigned_surveyor?.last_name || '').toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q)
      );
    }

    // Sort
    items.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'date') cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      else if (sortField === 'status') cmp = a.status.localeCompare(b.status);
      else if (sortField === 'type') cmp = a.service_type.localeCompare(b.service_type);
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return items;
  }, [requests, statusFilter, serviceTypeFilter, dateFrom, dateTo, search, sortField, sortDir]);

  const clearFilters = () => {
    setStatusFilter('ALL');
    setServiceTypeFilter('');
    setDateFrom('');
    setDateTo('');
    setSearch('');
  };

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
  };

  // Stats
  const totalRequests = requests.length;
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    requests.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [requests]);
  const serviceTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    requests.forEach((r) => { counts[r.service_type] = (counts[r.service_type] || 0) + 1; });
    return counts;
  }, [requests]);

  if (isLoading) {
    return <div style={s.center}><div style={s.spinner} /></div>;
  }

  return (
    <div>
      <style>{CSS}</style>
      <h1 style={s.title}>Platform Survey History</h1>
      <p style={s.subtitle}>Complete audit trail of all survey requests across the platform.</p>

      {/* KPIs */}
      <div style={s.kpiRow}>
        {[
          { label: 'Total Requests', val: totalRequests, icon: 'clipboard', color: 'var(--sc-brand-primary)' },
          { label: 'Confirmed', val: statusCounts['CONFIRMED'] || 0, icon: 'success', color: 'var(--sc-success)' },
          { label: 'In Progress', val: (statusCounts['ASSIGNED'] || 0) + (statusCounts['NEGOTIATING'] || 0), icon: 'tools', color: 'var(--sc-warning)' },
          { label: 'Disputed', val: statusCounts['DISPUTED'] || 0, icon: 'warning', color: 'var(--sc-error)' },
          { label: 'Total Value', val: summary ? `$${(summary.total_deal_value ?? 0).toFixed(0)}` : '—', icon: 'money', color: 'var(--sc-brand-primary-light)' },
        ].map((k) => (
          <div key={k.label} className="zs-stat-card">
            {/* Top color bar */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 3,
              background: k.color
            }} />
            {/* Decorative circle */}
            <div className="zs-stat-card-circle" style={{ backgroundColor: k.color, opacity: 0.05 }} />
            
            {/* Content inner */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1, gap: 4, textAlign: 'center' }}>
              <div className="zs-stat-card-iconbox" style={{ background: 'var(--sc-bg-primary)', color: k.color, border: `1px solid var(--sc-border)`, margin: '0 auto' }}>
                <AnimatedIcon name={k.icon as any} size={16} color={k.color} />
              </div>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--sc-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 1 }}>
                {k.label}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--sc-text-primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                {k.val}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Breakdowns */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
        {/* Status breakdown — Pie Chart */}
        <div style={{ ...s.breakdownCard, flex: '1 1 260px' }}>
          <h3 style={s.breakdownTitle}>📊 By Status</h3>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0' }}>
            <PieChart
              data={Object.entries(statusCounts).sort((a, b) => b[1] - a[1]).map(([status, count]) => ({
                label: status.replace(/_/g, ' '),
                value: count,
                color: STATUS_COLORS[status]?.color || 'var(--sc-text-secondary)',
              }))}
              size={96}
              ringWidth={30}
              centerSub="requests"
            />
          </div>
        </div>
        {/* Service type breakdown — Pie Chart */}
        <div style={{ ...s.breakdownCard, flex: '1 1 260px' }}>
          <h3 style={s.breakdownTitle}>🗂️ By Service Type</h3>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0' }}>
            <PieChart
              data={Object.entries(serviceTypeCounts).sort((a, b) => b[1] - a[1]).map(([type, count], i) => ({
                label: type,
                value: count,
                color: PIE_COLORS[i % PIE_COLORS.length],
              }))}
              size={96}
              ringWidth={0}
              centerSub="types"
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={s.filterRow}>
        <input
          placeholder="Search client, surveyor, location, ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={s.searchInput}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={s.select}>
          <option value="ALL">All Statuses</option>
          {Object.values(RequestState).map((st) => (
            <option key={st} value={st}>{st.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select value={serviceTypeFilter} onChange={(e) => setServiceTypeFilter(e.target.value)} style={s.select}>
          <option value="">All Service Types</option>
          {SURVEY_SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={s.dateInput} title="From date" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={s.dateInput} title="To date" />
        <button onClick={clearFilters} style={s.clearBtn}>Clear</button>
      </div>

      <div style={s.resultCount}>Showing {filtered.length} of {totalRequests} records</div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div style={s.empty}>No records match your filters.</div>
      ) : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <div style={s.table}>
            <div style={s.thead}>
              {[{ label: 'Service Type', flex: 1.5, field: 'type' as const }, { label: 'Client', flex: 1.5 }, { label: 'Surveyor', flex: 1.5 }, { label: 'Location', flex: 2.2 }, { label: 'Value', flex: 1 }, { label: 'Status', flex: 1.2, field: 'status' as const }, { label: 'Date', flex: 1.2, field: 'date' as const }].map(h => (
                <div key={h.label} style={{ ...s.th, flex: h.flex, cursor: h.field ? 'pointer' : 'default' }} onClick={h.field ? () => toggleSort(h.field) : undefined}>
                  {h.label} {h.field && sortField === h.field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </div>
              ))}
            </div>
            {filtered.map((req) => (
              <Link
                to={`/admin/requests/${req.id}`}
                key={req.id}
                style={{
                  ...s.row,
                  borderLeft: `4px solid ${(STATUS_COLORS[req.status]?.border || 'var(--sc-border)')}`
                }}
                className="zs-tr"
              >
                <div style={{ ...s.td, flex: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.service_type}</div>
                <div style={{ ...s.td, flex: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.client?.first_name} {req.client?.last_name}</div>
                <div style={{ ...s.td, flex: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {req.assigned_surveyor
                    ? `${req.assigned_surveyor.first_name} ${req.assigned_surveyor.last_name}`
                    : <span style={{ color: 'var(--sc-text-tertiary)' }}>Unassigned</span>
                  }
                </div>
                <div style={{ ...s.td, flex: 2.2, fontSize: 12, color: 'var(--sc-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.address}</div>
                <div style={{ ...s.td, flex: 1, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {req.deal_value != null ? `$${req.deal_value.toFixed(2)}` : '—'}
                </div>
                <div style={{ ...s.td, flex: 1.2 }}>
                  <span style={{
                    ...s.statusBadge,
                    background: (STATUS_COLORS[req.status]?.bg || 'var(--sc-border-light)'),
                    color: (STATUS_COLORS[req.status]?.color || 'var(--sc-text-secondary)'),
                    borderColor: (STATUS_COLORS[req.status]?.border || 'var(--sc-border)')
                  }}>
                    {req.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <div style={{ ...s.td, flex: 1.2, fontSize: 12, color: 'var(--sc-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {new Date(req.created_at).toLocaleDateString('en-ZW', { year: 'numeric', month: 'short', day: 'numeric' })}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.5px' },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: '0 0 20px', fontFamily: "'Inter', sans-serif" },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 20 },
  kpiCard: { display: 'block', background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: '20px 22px', border: '1px solid var(--sc-border)', textAlign: 'center' as const, boxShadow: 'var(--sc-shadow-sm)' },
  kpiIcon: { width: 44, height: 44, borderRadius: '50%', background: 'var(--sc-bg-primary)', border: '1px solid var(--sc-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' },
  kpiVal: { fontSize: 24, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '6px 0 2px' },
  kpiLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 600, textTransform: 'uppercase' as const },
  breakdownCard: { flex: 1, background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: '16px 20px', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)' },
  breakdownTitle: { fontSize: 14, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 12px', fontFamily: "'Outfit', sans-serif" },
  breakdownRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, position: 'relative' as const },
  statusDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  breakdownLabel: { fontSize: 12, color: 'var(--sc-text-primary)', flex: 1, textTransform: 'capitalize' as const },
  breakdownVal: { fontSize: 13, fontWeight: 700, color: 'var(--sc-text-primary)', minWidth: 28, textAlign: 'right' as const },
  breakdownBar: { position: 'absolute' as const, bottom: -2, left: 0, height: 2, borderRadius: 1, opacity: 0.4 },
  filterRow: { display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' as const, alignItems: 'center' },
  searchInput: { flex: 2, minWidth: 180, padding: '9px 12px', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 13, fontFamily: "'Inter', sans-serif", color: 'var(--sc-text-primary)', background: 'var(--sc-bg-input)' },
  select: { padding: '9px 10px', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 13, background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-primary)', outline: 'none' },
  dateInput: { padding: '9px 10px', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 13, color: 'var(--sc-text-primary)', background: 'var(--sc-bg-input)' },
  clearBtn: { padding: '9px 14px', background: 'var(--sc-bg-secondary)', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--sc-text-secondary)', transition: 'all 0.2s' },
  resultCount: { fontSize: 12, color: 'var(--sc-text-tertiary)', marginBottom: 14 },
  empty: { textAlign: 'center' as const, padding: 60, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-secondary)', borderRadius: 16, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)' },
  table: { background: 'var(--sc-bg-secondary)', borderRadius: 16, border: '1px solid var(--sc-border)', overflow: 'hidden', boxShadow: 'var(--sc-shadow-md)', minWidth: 800 },
  thead: { display: 'flex', padding: '10px 20px', background: 'var(--sc-bg-primary)', borderBottom: '1px solid var(--sc-border)' },
  th: { flex: 1, fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  row: { display: 'flex', padding: '13px 20px', borderBottom: '1px solid var(--sc-border)', alignItems: 'center', textDecoration: 'none', color: 'inherit', transition: 'background 0.15s' },
  td: { flex: 1, fontSize: 13, color: 'var(--sc-text-primary)' },
  statusBadge: { padding: '3px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' as const, whiteSpace: 'nowrap' as const, border: '1px solid' },
};

